import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoadWithInput } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { StorageLocationType } from "@redotech/redo-fulfillment-wms-common/schema/storage-location";
import { useTranslation } from "@redotech/redo-i18n";
import { type LocationId } from "@redotech/redo-inventory-id";
import { cancelLeavesNoItems } from "@redotech/redo-merchant-fulfillment-rpc/schema/cancel-order-items";
import { Provider } from "@redotech/redo-order-common/provider";
import { productIdFormat } from "@redotech/redo-product-id";
import { type StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { DisabledTooltip } from "@redotech/redo-web/tooltip/disabled-tooltip";
import { memo, useMemo, useState } from "react";
import {
  CancelItemBinCounts,
  type ProductBinCounts,
} from "../../common/cancel-order-items/cancel-item-bin-counts";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { summarizePickableBins } from "./problem-pick-source-allocation";

/** A problem-pick row: line items grouped by product, with how many were picked. */
export interface ProblemItemRow extends FulfillmentOrderLineItem {
  pickedQuantity: number;
  allOriginalItems: FulfillmentOrderLineItem[];
}

const BIN_PREVIEW_COUNT = 2;

/** Redo's pickable bins for a row's product, each with a shortcut to pick from it. */
const ProblemItemBinsCell = memo(function ProblemItemBinsCell({
  row,
  locationId,
  onPickFromBin,
  onViewAllBins,
  pickDisabledMessage,
  refreshKey,
}: {
  row: ProblemItemRow;
  locationId: LocationId;
  /** Omitted for rows that are already picked. */
  onPickFromBin?: (storageLocationId: StorageLocationId) => void;
  onViewAllBins: () => void;
  pickDisabledMessage: string | undefined;
  refreshKey: number;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const productId = productIdFormat.schema.safeParse(row.redoProductId).data;

  const binsLoad = useLoad(
    async (signal) => {
      if (!productId) {
        return [];
      }
      const { bins } = await rpcClient.getBinsForProduct(
        { locationId, productId, includeStorage: true, includeReceiving: true },
        { signal },
      );
      return bins;
    },
    [rpcClient, locationId, productId, refreshKey],
  );

  const bins = useMemo(
    () =>
      summarizePickableBins(
        binsLoad.value ?? [],
        row.imsInventoryItemId ?? null,
      ),
    [binsLoad.value, row.imsInventoryItemId],
  );

  if (!productId) {
    return <RedoTableTextCell text="—" textColor="secondary" />;
  }

  return (
    <Flex
      dir="column"
      gap="none"
      justify="center"
      onClick={(event) => event.stopPropagation()}
      px="md"
      w="full"
    >
      {binsLoad.pending ? (
        <Text fontSize="xs" textColor="tertiary">
          {t("Loading bins…")}
        </Text>
      ) : bins.length === 0 ? (
        <Text fontSize="xs" textColor="tertiary">
          {t("No pickable stock")}
        </Text>
      ) : (
        bins.slice(0, BIN_PREVIEW_COUNT).map((bin) => (
          <Flex
            align="center"
            gap="sm"
            justify="space-between"
            key={bin.storageLocationId}
            w="full"
          >
            <Flex align="center" gap="xs" overflow="hidden">
              <RedoBadge color="gray" size="xs" text={bin.binName} />
              <Text fontSize="xs" textColor="tertiary" whiteSpace="nowrap">
                {t("{{count}} avail.", { count: bin.availableQuantity })}
              </Text>
              {bin.binType === StorageLocationType.RECEIVING && (
                <RedoBadge color="blueLight" size="xs" text={t("Receiving")} />
              )}
            </Flex>
            {onPickFromBin && (
              <DisabledTooltip disabledMessage={pickDisabledMessage}>
                <RedoButton
                  data-dd-action-name="problem_order:pick_from_bin_click"
                  disabled={!!pickDisabledMessage}
                  hierarchy="link"
                  onClick={() => onPickFromBin(bin.storageLocationId)}
                  size="xs"
                  text={t("Pick")}
                />
              </DisabledTooltip>
            )}
          </Flex>
        ))
      )}
      <Flex>
        <RedoButton
          data-dd-action-name="problem_order:view_bins_click"
          hierarchy="secondary"
          onClick={onViewAllBins}
          size="xs"
          text={t("View all bins")}
        />
      </Flex>
    </Flex>
  );
});

/**
 * WMS source-of-truth teams see Redo's pickable bins with pick shortcuts;
 * everyone else sees the marketplace bin copied onto the line item.
 */
export const createBinLocationColumn = <Row extends ProblemItemRow>(
  t: (key: string) => string,
  wms?: {
    locationId: LocationId;
    onPickFromBin?: (row: Row, storageLocationId: StorageLocationId) => void;
    onViewAllBins: (row: Row) => void;
    pickDisabledMessage: string | undefined;
    /** Bump to reload every row's bins, e.g. after an inventory adjustment. */
    binsRefreshKey: number;
  },
): RedoTableColumn<Row> => ({
  key: "binLocation",
  widthMode: { type: "fixed", width: wms ? "300px" : "150px" },
  renderHeaderCell: () =>
    StandardRedoTableHeaderCell({
      headerText: wms ? t("Bins") : t("Bin location"),
    }),
  renderNormalCell: ({ row }) =>
    wms ? (
      <ProblemItemBinsCell
        locationId={wms.locationId}
        onPickFromBin={
          wms.onPickFromBin
            ? (storageLocationId) => wms.onPickFromBin?.(row, storageLocationId)
            : undefined
        }
        onViewAllBins={() => wms.onViewAllBins(row)}
        pickDisabledMessage={wms.pickDisabledMessage}
        refreshKey={wms.binsRefreshKey}
        row={row}
      />
    ) : (
      <RedoTableTextCell text={row.bin || "—"} textColor="secondary" />
    ),
  alignment: "left",
});

export const createCancelItemColumn = <Row extends ProblemItemRow>(
  t: (key: string) => string,
  onCancelItem: (row: Row) => void,
  getDisabledMessage: (row: Row) => string | undefined,
): RedoTableColumn<Row> => ({
  key: "cancelItem",
  widthMode: { type: "fixed", width: "160px" },
  renderHeaderCell: () => StandardRedoTableHeaderCell({ headerText: "" }),
  renderNormalCell: ({ row }) => (
    <Flex align="center" justify="center" p="lg">
      <DisabledTooltip disabledMessage={getDisabledMessage(row)}>
        <RedoButton
          disabled={!!getDisabledMessage(row)}
          hierarchy="tertiary"
          onClick={() => onCancelItem(row)}
          size="sm"
          text={t("Cancel & refund...")}
          theme="destructive"
        />
      </DisabledTooltip>
    </Flex>
  ),
  alignment: "center",
});

export function getCancelItemDisabledMessage({
  t,
  canEditOrder,
  fulfillmentGroup,
  row,
}: {
  t: (key: string) => string;
  canEditOrder: boolean;
  fulfillmentGroup: FulfillmentGroupData;
  row: ProblemItemRow;
}): string | undefined {
  if (!canEditOrder) {
    return t("You need the Edit orders permission to cancel items");
  }
  if (!fulfillmentGroup.orderSources.includes(Provider.SHOPIFY)) {
    return t("Only Shopify orders can have items cancelled");
  }
  if (
    cancelLeavesNoItems(
      fulfillmentGroup.items.map((item) => item.quantity),
      [row.quantity - row.pickedQuantity],
    )
  ) {
    return t(
      "This would leave no items on the order. Cancel the order instead.",
    );
  }
  return undefined;
}

export interface CancelItemOptions {
  restock: boolean;
  notifyCustomer: boolean;
  binCounts: ProductBinCounts | null;
}

/** Cancels and refunds a row's unpicked quantity, toasting the outcome. */
export function useCancelUnpickedItem({
  fulfillmentGroupId,
  pickedQuantityByExternalId,
  onSettled,
  onCancelled,
}: {
  fulfillmentGroupId: string;
  pickedQuantityByExternalId: Map<string, number>;
  onSettled: () => void;
  onCancelled?: () => void;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  return useTriggerLoadWithInput(
    async (input: { row: ProblemItemRow } & CancelItemOptions, signal) => {
      const items = input.row.allOriginalItems.flatMap((item) => {
        const quantity =
          item.quantity -
          (pickedQuantityByExternalId.get(item.externalId) || 0);
        return quantity > 0
          ? [{ fulfillmentOrderLineItemId: item.externalId, quantity }]
          : [];
      });
      const toastOptions = { duration: Temporal.Duration.from({ seconds: 3 }) };
      try {
        const result = await rpcClient.cancelOrderItems(
          {
            fulfillmentGroupId,
            items,
            restock: input.restock,
            notifyCustomer: input.notifyCustomer,
            binCounts: input.binCounts,
          },
          { signal },
        );
        if (result.success && result.binCountsFailed) {
          toast(
            t(
              "Cancelled and refunded the item, but the bin counts could not be updated",
            ),
            { ...toastOptions, variant: "warning" },
          );
        } else if (result.success) {
          toast(
            t("Cancelled and refunded {{quantity}} x {{item}}", {
              quantity: input.row.quantity - input.row.pickedQuantity,
              item: input.row.productTitle,
            }),
            { ...toastOptions, variant: "success" },
          );
        } else {
          toast(t("Failed to cancel item"), {
            ...toastOptions,
            variant: "error",
          });
        }
        if (result.success) {
          onCancelled?.();
        }
      } catch {
        toast(t("Failed to cancel item"), {
          ...toastOptions,
          variant: "error",
        });
      } finally {
        onSettled();
      }
    },
  );
}

export const CancelItemModal = memo(function CancelItemModal({
  productTitle,
  quantity,
  wmsSot,
  crdbLocationId,
  redoProductId,
  defaultBinName,
  onClose,
  onConfirm,
  loading,
}: {
  productTitle: string;
  quantity: number;
  /** Redo owns on-hand here, so offer bin counts instead of a Shopify restock. */
  wmsSot: boolean;
  crdbLocationId: string | null | undefined;
  redoProductId: string | null | undefined;
  /** The bin the item was expected in, checked by default. */
  defaultBinName: string | null | undefined;
  onClose: () => void;
  onConfirm: (options: CancelItemOptions) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [restock, setRestock] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [binCounts, setBinCounts] = useState<ProductBinCounts | null>(null);

  const handleConfirm = () => {
    onConfirm({
      restock: wmsSot ? false : restock,
      notifyCustomer,
      binCounts: wmsSot ? binCounts : null,
    });
  };

  return (
    <RedoModal
      isOpen
      modalSize={wmsSot ? "md" : "sm"}
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: handleConfirm,
        pending: loading,
        text: t("Cancel & refund"),
        theme: "destructive",
        size: "md",
      }}
      secondaryButton={{ onClick: onClose, text: t("Back"), size: "md" }}
      title={t("Cancel & refund item")}
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          {t(
            "This removes {{quantity}} x {{item}} from the order and refunds the customer for it.",
            { quantity, item: productTitle },
          )}
        </Text>
        <RedoCheckbox
          label={t("Notify customer")}
          setValue={(value) => setNotifyCustomer(value === true)}
          size="sm"
          value={notifyCustomer}
        />
        {wmsSot ? (
          <Flex dir="column" gap="md">
            <Flex dir="column" gap="xxs">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Adjust bin inventory (optional)")}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {t(
                  "Set the counted quantity for bins that don't have this item.",
                )}
              </Text>
            </Flex>
            <CancelItemBinCounts
              crdbLocationId={crdbLocationId}
              defaultBinName={defaultBinName}
              onChange={setBinCounts}
              redoProductId={redoProductId}
            />
          </Flex>
        ) : (
          <RedoCheckbox
            label={t("Restock items")}
            setValue={(value) => setRestock(value === true)}
            size="sm"
            value={restock}
          />
        )}
      </Flex>
    </RedoModal>
  );
});
